import { vec4, mat4, vec3 } from 'gl-matrix';
import { Face, Vertex } from '../geometry';

export function project(
  position: vec3,
  projectionMatrix: mat4,
  modelViewMatrix: mat4,
  dimensions: { width: number; height: number },
): vec4 {
  // Calculate the 3D position by transforming the location in the same way as we do in the shader

  // Shader: gl_Position = pMatrix * mvMatrix * vec4(vertexPosition.xyz, 1.0);
  // Javascript equivalent:
  let convertedPosition = vec4.transformMat4(
    vec4.create(),
    [position[0], position[1], position[2], 1],
    mat4.multiply(mat4.create(), projectionMatrix, modelViewMatrix),
  );

  // Perspective divide: clip space → NDC → screen space
  const w = convertedPosition[3];
  convertedPosition[0] = (convertedPosition[0] / w + 1.0) * 0.5 * dimensions.width;
  convertedPosition[1] = (convertedPosition[1] / w + 1.0) * 0.5 * dimensions.height;
  convertedPosition[2] /= w;

  return convertedPosition;
}

export function calculateNormal(
  vecA: vec3 | number[],
  vecB: vec3 | number[],
  vecC: vec3 | number[],
): vec3 {
  let u = vec3.subtract(vec3.create(), vecB, vecA);
  let v = vec3.subtract(vec3.create(), vecC, vecA);

  return vec3.cross(u, u, v);
}

export function unproject(
  position: vec3,
  projectionMatrix: mat4,
  modelViewMatrix: mat4,
  dimensions: { width: number; height: number },
): vec4 {
  let x = (position[0] / dimensions.width) * 2 - 1;
  let y = (position[1] / dimensions.height) * 2 - 1;
  let z = position[2] * 2 - 1;

  const inverse = mat4.invert(
    mat4.create(),
    mat4.multiply(mat4.create(), projectionMatrix, modelViewMatrix),
  );
  if (inverse === null) {
    // Singular projection × modelview — happens for degenerate cameras
    // (zero scale, collapsed frustum). Without this guard the `!` would
    // silently feed garbage into transformMat4 and yield NaN positions.
    throw new Error('unproject: projection × modelView is singular and cannot be inverted');
  }
  let convertedPosition = vec4.transformMat4(vec4.create(), [x, y, z, 1], inverse);

  convertedPosition[0] /= convertedPosition[3];
  convertedPosition[1] /= convertedPosition[3];
  convertedPosition[2] /= convertedPosition[3];

  return convertedPosition;
}

let boxNormals = [vec3.fromValues(1, 0, 0), vec3.fromValues(0, 1, 0), vec3.fromValues(0, 0, 1)];

let edgeVecs = [vec3.create(), vec3.create(), vec3.create()];
let axisVecs: vec3[][] = [];
for (let i = 0; i < 3; i++) {
  axisVecs.push([]);
  for (let j = 0; j < 3; j++) {
    axisVecs[i].push(vec3.create());
  }
}

let positions: vec3[] = [];
for (let i = 0; i < 8; i++) {
  positions.push(vec3.create());
}

function fillPositions(min: vec3 | number[], max: vec3 | number[]) {
  positions[0][0] = min[0];
  positions[0][1] = min[1];
  positions[0][2] = min[2];

  positions[1][0] = min[0];
  positions[1][1] = min[1];
  positions[1][2] = max[2];

  positions[2][0] = min[0];
  positions[2][1] = max[1];
  positions[2][2] = min[2];

  positions[3][0] = max[0];
  positions[3][1] = min[1];
  positions[3][2] = min[2];

  positions[4][0] = min[0];
  positions[4][1] = max[1];
  positions[4][2] = max[2];

  positions[5][0] = max[0];
  positions[5][1] = min[1];
  positions[5][2] = max[2];

  positions[6][0] = max[0];
  positions[6][1] = max[1];
  positions[6][2] = min[2];

  positions[7][0] = max[0];
  positions[7][1] = max[1];
  positions[7][2] = max[2];

  //let coords = [
  //    vec3.fromValues(min[0], min[1], min[2]),
  //    vec3.fromValues(min[0], min[1], max[2]),
  //    vec3.fromValues(min[0], max[1], min[2]),
  //    vec3.fromValues(max[0], min[1], min[2]),
  //    vec3.fromValues(min[0], max[1], max[2]),
  //    vec3.fromValues(max[0], min[1], max[2]),
  //    vec3.fromValues(max[0], max[1], min[2]),
  //    vec3.fromValues(max[0], max[1], max[2])
  //];
}

//https://stackoverflow.com/questions/17458562/efficient-aabb-triangle-intersection-in-c-sharp
export function intersectsFace(box: BoundingBoxLike, face: Face) {
  fillPositions(box.min, box.max);

  for (let i = 0; i < 3; i++) {
    let axisResult = checkAxis(face.vertices, boxNormals[i]);
    if (axisResult.max < box.min[i] || axisResult.min > box.max[i]) {
      return false;
    }
  }

  let triangleOffset = vec3.dot(face.normal, face.vertices[0].position);
  let offsetResult = checkAxis(positions, face.normal);
  if (offsetResult.max < triangleOffset || offsetResult.min > triangleOffset) {
    return false;
  }

  // Test the nine edge cross-products
  let edges = [
    vec3.subtract(edgeVecs[0], face.vertices[0].position, face.vertices[1].position),
    vec3.subtract(edgeVecs[1], face.vertices[1].position, face.vertices[2].position),
    vec3.subtract(edgeVecs[2], face.vertices[2].position, face.vertices[0].position),
  ];

  for (let i = 0; i < 3; i++) {
    for (let j = 0; j < 3; j++) {
      // The box normals are the same as it's edge tangents
      let axis = vec3.cross(axisVecs[i][j], edges[i], boxNormals[j]);
      let boxResult = checkAxis(positions, axis);
      let faceResult = checkAxis(face.vertices, axis);

      if (boxResult.max < faceResult.min || boxResult.min > faceResult.max) {
        return false;
      }
    }
  }

  // No separating axis found.
  return true;
}

type BoundingBoxLike = {
  min: vec3 | number[];
  max: vec3 | number[];
};

// TODO: what does this do exactly and what would be an appropriate name for it?
function checkAxis(vertices: vec3[] | Vertex[], axisVector: vec3) {
  let vs: any = vertices;
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < vertices.length; i++) {
    let val = vec3.dot(axisVector, vs[i].position ? vs[i].position : vs[i]);
    if (val < min) {
      min = val;
    }
    if (val > max) {
      max = val;
    }
  }

  return {
    min: min,
    max: max,
  };
}

const degree = Math.PI / 180;

export function toRadian(a: number) {
  return a * degree;
}

export function nextPow2(v: number): number {
  let n = 1;
  while (n < v) n <<= 1;
  return n;
}
